import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  email: string;
  fullName: string;
  phone?: string;
  role: 'customer';
}

interface ClientAuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  setAuth: (token: string, user: User) => void;
  logout: () => void;
  checkTokenValidity: () => boolean;
}

interface RegisterData {
  email: string;
  password: string;
  fullName: string;
  phone?: string;
  dni?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.centroautomotrizhlfrd.com.do/api';

export const useClientAuthStore = create<ClientAuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/customer/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
          });

          if (!response.ok) {
            let message = 'Credenciales incorrectas';
            try {
              const data = await response.json();
              if (data?.error) message = data.error;
            } catch {
              const text = await response.text();
              if (text) message = text;
            }
            throw new Error(message);
          }

          const data = await response.json();
          
          set({
            user: { ...data.user, role: 'customer' },
            token: data.token,
            isAuthenticated: true
          });
        } catch (error) {
          console.error('Login error:', error);
          throw error;
        }
      },

      register: async (data: RegisterData) => {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/customer/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
          });

          if (!response.ok) {
            let message = 'Error al registrar usuario';
            try {
              const err = await response.json();
              if (err?.error) message = err.error;
            } catch {
              const text = await response.text();
              if (text) message = text;
            }
            throw new Error(message);
          }

          // Registro exitoso: el backend devuelve token y usuario, iniciar sesión directamente.
          const result = await response.json();
          set({
            user: { ...result.user, role: 'customer' },
            token: result.token,
            isAuthenticated: true
          });
        } catch (error) {
          console.error('Register error:', error);
          throw error;
        }
      },

      setAuth: (token: string, user: User) => {
        set({
          user: { ...user, role: 'customer' },
          token,
          isAuthenticated: true
        });
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false
        });
      },

      checkTokenValidity: () => {
        const state = useClientAuthStore.getState();
        if (!state.token) {
          return false;
        }

        try {
          const payload = JSON.parse(atob(state.token.split('.')[1]));
          const currentTime = Date.now() / 1000;

          if (payload.exp < currentTime) {
            // Token expirado, hacer logout automático
            state.logout();
            return false;
          }

          return true;
        } catch (error) {
          console.error('Error validating token:', error);
          state.logout();
          return false;
        }
      }
    }),
    {
      name: 'client-auth-storage'
    }
  )
);